import {Checkbox} from '@wandb/weave/components';
import {Pill, type PillProps} from '@wandb/weave/components/Tag';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';

import {reportTriggerConfigParseError} from '../configParseError';
import {EventTriggeringConditionType} from '../TriggerEventTypes';
import {type Trigger} from '../TriggersTable/utilTypes';
import {BoldLabel, SeparatedChunk, SmallText} from './shared';

// The states a SANDBOX_STATE event reports. A stopped sandbox is COMPLETED.
// NOTE: this order is used for determining how to generate strings for the UI
// so be careful when changing it
const SANDBOX_STATES = ['CREATED', 'COMPLETED', 'FAILED'] as const;

export type SandboxStateToReport = (typeof SANDBOX_STATES)[number];

export type SandboxStateConfig = {
  states: SandboxStateToReport[];
  // Predicates in a saved filter that the editor does not show, such as
  // previous_status from an automation created through the API. They are
  // saved back unchanged so editing the automation does not broaden it.
  otherPredicates?: Record<string, unknown>;
};

const initialSandboxStateConfig: SandboxStateConfig = {
  states: [],
};

export const useSandboxStateStore = create<SandboxStateConfig>()(
  devtools(
    () => ({
      ...initialSandboxStateConfig,
    }),
    {name: 'SandboxStateStore'}
  )
);

export const SandboxStateActions = {
  // Both replace the whole store rather than merging into it, so a new or
  // unparseable automation does not inherit the previously opened one's
  // states or hidden predicates.
  init: () => {
    useSandboxStateStore.setState({...initialSandboxStateConfig}, true);
  },
  initFromTrigger: (trigger: Trigger) => {
    const parsed = parseSandboxStateFilter(trigger);
    // A state predicate the editor cannot show starts with no states selected,
    // so the user must choose them; the other predicates are still kept.
    useSandboxStateStore.setState(
      parsed
        ? {
            states: parsed.states ?? [],
            ...(parsed.otherPredicates && {
              otherPredicates: parsed.otherPredicates,
            }),
          }
        : {...initialSandboxStateConfig},
      true
    );
  },
  updateStateToReport: (state: SandboxStateToReport, isSelected: boolean) => {
    const currentStates = useSandboxStateStore.getState().states;
    const newStates = isSelected
      ? currentStates.includes(state)
        ? currentStates
        : [...currentStates, state]
      : currentStates.filter(s => s !== state);
    useSandboxStateStore.setState({states: newStates});
  },
};

// Compact pills like the run status ones, with the sandboxes page's colors. A
// sandbox is created once it is placed and starting, so CREATED looks like
// running.
const STATE_PILLS: Record<SandboxStateToReport, PillProps> = {
  CREATED: {label: 'Created', icon: 'running-repeat', color: 'blue'},
  COMPLETED: {label: 'Completed', icon: 'checkmark-circle', color: 'green'},
  FAILED: {label: 'Failed', icon: 'failed', color: 'red'},
};

const STATE_DESCRIPTIONS: Record<SandboxStateToReport, string> = {
  CREATED: 'Created and starting',
  COMPLETED: 'Exited cleanly or was stopped',
  FAILED: 'Failed to start, exited with an error, or reached its lifetime',
};

export const SandboxStateSection = () => {
  const states = useSandboxStateStore(state => state.states);
  return (
    <Tailwind>
      <SeparatedChunk>
        <BoldLabel>Included states</BoldLabel>
        <div className="mb-8 mt-4">
          <SmallText>
            This event will trigger whenever a sandbox in this project reaches
            one of the selected states.
          </SmallText>
        </div>
        {SANDBOX_STATES.map(state => (
          <div key={state} className="flex flex-row items-center gap-8">
            <div className="my-4 flex flex-row items-center gap-8">
              <Checkbox
                size="small"
                aria-label={state.toLowerCase()}
                checked={states.includes(state)}
                onCheckedChange={checked => {
                  SandboxStateActions.updateStateToReport(
                    state,
                    checked !== false
                  );
                }}
              />
            </div>
            <Pill {...STATE_PILLS[state]} />
            <SmallText>{STATE_DESCRIPTIONS[state]}</SmallText>
          </div>
        ))}
      </SeparatedChunk>
    </Tailwind>
  );
};

export const isValidSandboxStateConfig = (
  sandboxStateConfig: SandboxStateConfig
) => {
  return (
    sandboxStateConfig.states.length > 0 &&
    sandboxStateConfig.states.every(state => SANDBOX_STATES.includes(state))
  );
};

export const getSandboxStateString = (
  sandboxStateConfig: SandboxStateConfig
) => {
  const states = SANDBOX_STATES.filter(state =>
    sandboxStateConfig.states.includes(state)
  ).map(s => s.toLocaleLowerCase());
  const stateList =
    states.length <= 2
      ? states.join(' or ')
      : states.slice(0, -1).join(', ') + ', or ' + states[states.length - 1];
  return `a sandbox is ${stateList}.`;
};

// The filter the backend matches against a sandbox event's state.
export const getSandboxStateFilter = (
  sandboxStateConfig: SandboxStateConfig
) => {
  return {
    ...sandboxStateConfig.otherPredicates,
    state: {$in: sandboxStateConfig.states},
  };
};

// The states a saved state predicate selects, or undefined if the editor
// cannot represent it exactly (another operator, or a state it does not offer).
const parseStatePredicate = (
  state: unknown
): SandboxStateToReport[] | undefined => {
  const values: unknown =
    typeof state === 'string'
      ? [state]
      : state !== null &&
          typeof state === 'object' &&
          Object.keys(state).length === 1 &&
          '$in' in state
        ? state.$in
        : undefined;
  if (
    !Array.isArray(values) ||
    values.length === 0 ||
    !values.every(v => (SANDBOX_STATES as readonly unknown[]).includes(v))
  ) {
    return undefined;
  }
  return SANDBOX_STATES.filter(s => values.includes(s));
};

// Splits a saved filter into the states the editor shows and the predicates it
// keeps unchanged. states is undefined when the state predicate cannot be shown
// exactly; the result is undefined when the filter cannot be read at all.
const parseSandboxStateFilter = (
  trigger: Trigger
):
  | {
      states: SandboxStateToReport[] | undefined;
      otherPredicates: Record<string, unknown> | undefined;
    }
  | undefined => {
  if (
    trigger.triggeringCondition.eventType !==
    EventTriggeringConditionType.SandboxState
  ) {
    return undefined;
  }
  if (!trigger.triggeringCondition.filter) {
    reportTriggerConfigParseError('sandbox_state', trigger, 'empty_filter');
    return undefined;
  }
  try {
    // The backend stores the filter nested as a JSON string under "filter".
    let parsedFilter = JSON.parse(trigger.triggeringCondition.filter);
    if (typeof parsedFilter?.filter === 'string') {
      parsedFilter = JSON.parse(parsedFilter.filter);
    }
    if (
      parsedFilter === null ||
      typeof parsedFilter !== 'object' ||
      Array.isArray(parsedFilter)
    ) {
      throw new Error('filter is not an object');
    }
    const {state, ...otherPredicates} = parsedFilter;
    return {
      states: parseStatePredicate(state),
      otherPredicates:
        Object.keys(otherPredicates).length > 0 ? otherPredicates : undefined,
    };
  } catch (e) {
    reportTriggerConfigParseError('sandbox_state', trigger, e);
    return undefined;
  }
};

// The editor's view of a saved filter, or undefined if it cannot show the
// filter's state predicate exactly.
export const getSandboxStateConfig = (
  trigger: Trigger
): SandboxStateConfig | undefined => {
  if (
    trigger.triggeringCondition.eventType !==
    EventTriggeringConditionType.SandboxState
  ) {
    return undefined;
  }
  const parsed = parseSandboxStateFilter(trigger);
  if (!parsed) {
    // parseSandboxStateFilter has already reported an unreadable filter.
    return undefined;
  }
  if (!parsed.states) {
    reportTriggerConfigParseError('sandbox_state', trigger);
    return undefined;
  }
  return parsed.otherPredicates
    ? {states: parsed.states, otherPredicates: parsed.otherPredicates}
    : {states: parsed.states};
};
